import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import TaskManager, { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import { PlayerSlideRenderTree } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/player/slide-tree';
import { Application } from '@tencent/alloy-renderer';
import { CANVAS_PLAYER_ID } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import { BrowserTarget } from '@tencent/alloy-renderer-browser';
import { MobilePlayerSlideRenderTree } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/player-mobile/slide-tree';
import { IPlayerSlideRenderTree, UserTransform } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/player/type';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { observable } from 'mobx';
import { ISlideComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/types';
import {
    IPlayerRendererService,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/player/player-renderer-service.interface';

export class PlayerRenderService implements IPlayerRendererService {
    public static instance: PlayerRenderService;
    public static getInstance(): PlayerRenderService {
        if (!PlayerRenderService.instance) {
            PlayerRenderService.instance = new PlayerRenderService();
        }
        return PlayerRenderService.instance;
    }

    @observable
    public userTransform: UserTransform = {
        scaleX: 1,
        scaleY: 1,
        pivotX: 0,
        pivotY: 0,
    };

    private playerReduxStore: any;

    private renderTreeMap: Map<string, IPlayerSlideRenderTree> = new Map();

    private currentSlide: ISlideModel | null = null;
    private canvasDom?: HTMLCanvasElement;
    private application?: Application<any>;
    private slideRenderTree: IPlayerSlideRenderTree | undefined;
    private lastSlideRenderTree: IPlayerSlideRenderTree | undefined;

    // 前一页的canvass缓存。适用于需要前一页的渲染结果做处理的情景，如切页动画
    // 直接通过renderTree取，可能会因为清除策略而无法命中缓存，所以这里存一个作为稳定缓存。这是暂时方案
    // 后面如果有计划做player的重构，应该把这些缓存策略、以及逻辑控制等都抽出去，目前Player的逻辑和时序控制过于分散，维护成本大
    private prevSlideCanvasCache?: HTMLElement;

    public initWithStore(): void {
        this.createApp();
    }

    // 图形的操作需要player的redux，因为层级问题，目前需要动态注入进来
    public setPlayerReduxStore(playerReduxStore: any): void {
        this.playerReduxStore = playerReduxStore;
    }

    public getPlayerReduxStore() {
        return this.playerReduxStore;
    }

    public clearPlayerReduxStore(): void {
        this.playerReduxStore = null;
    }

    public destroyLayer(): void {
        this.getAppServices().viewableServiceLayer.animManager.playSlideComponent = undefined;
        const cachedKeys = Array.from(this.renderTreeMap.keys());
        for (let i = cachedKeys.length - 1; i >= 0; i--) {
            const currentKey = cachedKeys[i];
            const renderTree = this.renderTreeMap.get(currentKey);
            // 先删除，防止异步产生影响
            this.renderTreeMap.delete(currentKey);
            renderTree?.dispose();
        }
        this.application?.dispose();
        this.application = undefined;
        this.slideRenderTree = undefined;
        this.lastSlideRenderTree = undefined;
        this.canvasDom = undefined;
        this.clearPrevSlideCanvasCache();
    }

    /**
     * 切换到新页面，执行缓存淘汰策略
     * @param playIndex
     * @param onDidRenderCallback
     */
    public applyStrategy(playIndex: number, onDidRenderCallback: (slideComponent: ISlideComponent) => void): void {
        const { presentation } = this.getAppServices().getUiDataService();
        const needCachedSlideMap: Map<string, boolean> = new Map();
        // 转换相邻的页面的数据
        if (playIndex < presentation.children.length && playIndex >= 0) {
            const currentSlideModel = presentation.children[playIndex];
            needCachedSlideMap.set(currentSlideModel.key, true);
            this.currentSlide = currentSlideModel;
            // 当前页要同步创建
            this.buildSlideRenderTree(false, currentSlideModel, onDidRenderCallback);
        }
        // 相邻两页要异步创建
        if (playIndex > 0) {
            const preSlideModel = presentation.children[playIndex - 1];
            needCachedSlideMap.set(preSlideModel.key, true);
            this.buildSlideRenderTree(true, preSlideModel, onDidRenderCallback);
        }
        if (playIndex + 1 < presentation.children.length) {
            const nextSlideModel = presentation.children[playIndex + 1];
            needCachedSlideMap.set(nextSlideModel.key, true);
            this.buildSlideRenderTree(true, nextSlideModel, onDidRenderCallback);
        }
        // 清除其他页面的数据
        const cachedKeys = Array.from(this.renderTreeMap.keys());

        for (let i = cachedKeys.length - 1; i >= 0; i--) {
            const currentKey = cachedKeys[i];
            if (!needCachedSlideMap.has(currentKey)) {
                const renderTree = this.renderTreeMap.get(currentKey);
                // 先删除，防止异步产生影响
                this.renderTreeMap.delete(currentKey);
                if (browser.isMobile) {
                    renderTree?.dispose();
                } else {
                    // canvas渲染树有可能多次创建，销毁渲染树的任务key需要带上id，才能保证每次创建的渲染树都能添加到任务队列中
                    TaskManager.addTaskWithBlock(`dispose-player-tree-${currentKey}-${renderTree?.id}`, TaskPriority.LOW, () => {
                        renderTree?.dispose();
                    });
                }
            }
        }
    }

    /**
     * 当前页同步渲染
     * @param slideModel
     * @param canvasWrapperDom
     * @param scale
     * @param handleSlideClick
     */
    public display(
        slideModel: ISlideModel,
        canvasWrapperDom: HTMLDivElement,
        scale: number,
        handleSlideClick,
        onDidRenderCallback: (slideComponent: ISlideComponent) => void,
    ): void {
        const hasBuild = this.renderTreeMap.has(slideModel.key);
        if (!hasBuild) {
            // 当前页要同步创建
            this.buildSlideRenderTree(false, slideModel, onDidRenderCallback);
        }
        this.slideRenderTree = this.renderTreeMap.get(slideModel.key);
        this.getAppServices().viewableServiceLayer.playManager.currentSlideModel = slideModel;
        if (this.slideRenderTree) {
            this.lastSlideRenderTree?.hidden?.();
            this.slideRenderTree.display(canvasWrapperDom, scale, handleSlideClick, this.userTransform);
            this.getAppServices().viewableServiceLayer
                .animManager.playSlideComponent = this.slideRenderTree.slideComponent;
            this.lastSlideRenderTree = this.slideRenderTree;
        }
    }

    /**
     * set transform for Stage
     * 目前只需求 scale/pivot; 其中 scale 会与 slide 的 scale 叠加
     */
    public setTransform(userTransform: UserTransform): void {
        this.userTransform = userTransform;
        this.slideRenderTree?.setTransform(userTransform);
    }

    public getRenderTree(slideModel: ISlideModel): IPlayerSlideRenderTree | undefined {
        return this.renderTreeMap.get(slideModel.key);
    }

    public getPrevSlideCanvas() {
        return this.prevSlideCanvasCache;
    }

    public setPrevSlideCanvasCache(currentIndex: number, prevIndex: number) {
        try {
            if (currentIndex === prevIndex) return;
            const { presentation } = this.getAppServices().getUiDataService();
            const prevSlideModel = presentation.children[prevIndex];

            if (prevSlideModel) {
                const renderTree = this.getRenderTree(prevSlideModel);
                this.prevSlideCanvasCache = this
                    .createCanvasCacheFormRenderResult(renderTree?.getApplication()?.view?.native);
            } else {
                this.clearPrevSlideCanvasCache();
            }
        } catch (err) {
            // 有canvas相关的执行报错之后，清空缓存
            this.clearPrevSlideCanvasCache();
        }
    }

    /**
     * 获取指定页UI层的slideModel
     * @param slideIndex  页面索引
     * @returns ISlideModel
     */
    public getSlideModelByIndex(slideIndex: number): ISlideModel {
        const { slideModelList } = this.getAppServices().getUiDataService();
        return slideModelList[slideIndex];
    }

    private createCanvasCacheFormRenderResult(renderResultCanvas?: HTMLCanvasElement) {
        // 可能存在一些销毁策略，导致canvas实例存在但是宽高为0，会导致预期之外的问题。这里再判断下canvas的大小不为0再去缓存渲染结果
        if (!renderResultCanvas || renderResultCanvas.width === 0 || renderResultCanvas.height === 0) return;

        let canvas = this.prevSlideCanvasCache as HTMLCanvasElement;
        if (!this.prevSlideCanvasCache) {
            canvas = document.createElement('canvas');
        }

        canvas.width = renderResultCanvas.width;
        canvas.height = renderResultCanvas.height;
        canvas.style.cssText = renderResultCanvas.style.cssText;

        const canvasContext = canvas.getContext('2d');
        canvasContext?.drawImage(renderResultCanvas, 0, 0);

        return canvas;
    }


    /**
     * 创建渲染树，如果是当前页需要传递sync，
     */
    private buildSlideRenderTree(
        asyncBuild: boolean,
        slideModel: ISlideModel,
        onDidRenderCallback: (slideComponent: ISlideComponent) => void,
    ): void {
        if (!this.renderTreeMap.has(slideModel.key)) {
            const SlideRenderTree = browser.isMobile ? MobilePlayerSlideRenderTree : PlayerSlideRenderTree;
            const slideRenderTree = new SlideRenderTree({
                slideModel,
                asyncBuild,
                getPlayerReduxStore: () => this.playerReduxStore,
                canvasDom: this.canvasDom,
                application: this.application,
                onDidRenderCallback,
            });
            this.renderTreeMap.set(slideModel.key, slideRenderTree);
        }
    }

    /**
     * 移动端在slide-tree外部创建统一application
     */
    private createApp() {
        if (!browser.isMobile) {
            return;
        }
        if (!this.application) {
            this.canvasDom = document.createElement('canvas');
            this.canvasDom.id = CANVAS_PLAYER_ID;

            const target = new BrowserTarget();
            this.application = new Application(target, {
                interactive: true,
                native: this.canvasDom,
                delayDrawHit: false,
            });
            this.application.resolution = Math.ceil(window.devicePixelRatio);
            // 移动端演示态经常无法点击链接跳转，先调大 resolution
            this.application.hitView.resolution = window.devicePixelRatio;
        }
    }

    private clearPrevSlideCanvasCache() {
        this.prevSlideCanvasCache = undefined;
    }

    private getAppServices(): ISlideBaseAppServices {
        return AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices;
    }
}
